import React, {useState, useEffect} from 'react';
import RadioGroup from '@mui/material/RadioGroup';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import AddressComponent from './addressComponent';
import AddIcon from '@mui/icons-material/Add';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HomeIcon from '@mui/icons-material/Home';
import CloseIcon from '@mui/icons-material/Close';
import RoundedButton from '../../buttons/roundedButton';
import { inject, observer } from "mobx-react";
import AddOrEditaddressModal from '../../modals/addOrEditAddressModal/AddOrEditAddressModal';
import loadGoogleMaps from '../../utils/mapsLoader';
import { extractAddress, matchTenantCity } from '../../utils/geo';
import { DEFAULT_EMAIL } from '../../assets/globalConstants';

// Translations
import _useTranslate from '../../hooks/_useTranslate';

const Address = ({onClickClose, onClickAddNew, onClickLocation, feathersStore}) => {

  let account = _useTranslate(feathersStore.language, "account");
  let common = _useTranslate(feathersStore.language, "common");

  const [addresses, setAddresses] = useState([]);  
  const [showButtons, setShowButtons] = useState(false);
  const [visibleAddNewAddress, setAddNewAddress] = useState(false);
  const [prefill, setPrefill] = useState(null);

  /* "My location": ask the browser for a fix, reverse-geocode it with the
     TENANT'S key and open the address form prefilled. This button has existed
     (and been translated) all along but Account.js renders <Address/> with no
     props, so onClickLocation was undefined and it did nothing. */
  const handleMyLocation = () => {
    if(!feathersStore.canGeocode() || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(async (position) => {
      feathersStore.setHasLocationPermission(true);   // the observable existed but was never set
      const { latitude, longitude } = position.coords;
      try{
        const maps = await loadGoogleMaps(feathersStore.tenantMapsKey());
        if(!maps) return;
        const geocoder = new maps.Geocoder();
        const { results } = await geocoder.geocode({ location: { lat: latitude, lng: longitude } });
        const parsed = results?.[0] ? extractAddress(results[0].address_components) : {};
        const matched = matchTenantCity(
          parsed.cityCandidates, feathersStore.settings?.citys,
          (c) => feathersStore._translate(c.name, c.nameEnglish));
        setPrefill({
          street: parsed.street || '',
          streetNumber: parsed.streetNumber || '',
          postcode: parsed.postcode || '',
          city: matched ? matched.id : undefined,
          latitude,
          longitude
        });
        setAddNewAddress(true);
      }catch(error){ console.log('reverse geocoding failed', error); }
    }, (error) => {
      feathersStore.setHasLocationPermission(false);
      console.log('geolocation unavailable', error?.message);
    }, { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 });
  };

  useEffect(() => {
    feathersStore.user?.addresses && setAddresses([...feathersStore.user?.addresses]);
  }, [feathersStore.user?.addresses])

  const openNewAddress = () => {     
    setAddNewAddress(true); 
  };

  const closeNewAddress = () =>{
    setAddNewAddress(false);
    setPrefill(null);
  }

  const ClickAddAddress = ()=> {
      setShowButtons(!showButtons);
  }

  const setDeliveryAddress = item => () => {    
    const index = addresses.indexOf(item);
    const activeIndex = addresses.findIndex(e => e.active === true);
    let clonedAddresses = [...addresses];

    if (activeIndex !== index) {
      clonedAddresses[activeIndex].active = false;
      clonedAddresses[index].active = true;      
      updateUser(clonedAddresses);   
    }
  };

  const updateUser = async newAddresses => {
    // Guard before the optimistic local write, not just before the patch: a guest would
    // otherwise see the change take effect and only then be told it cannot be saved.
    if(feathersStore.user?.email === DEFAULT_EMAIL){
      alert(account.registerToEdit);
      return;
    }
    setAddresses([...newAddresses]);
    let clonedUser = JSON.parse(JSON.stringify(feathersStore.user));
    clonedUser.addresses = [...newAddresses];
    feathersStore.setUser(clonedUser); 
    await saveActiveAddress(newAddresses); 
  }

  const saveActiveAddress = async val => {
    await feathersStore.patchAddresses( feathersStore.user._id, [...val]);
  };

  const findCity = item => {
    const city =  feathersStore.settings.citys.find(c => +c.id === +item.city )
    if(city) return feathersStore._translate(city.name, city.nameEnglish);
    else return "";
  } 
   
  const renderAddressItem = ( item, index ) => (
   
    <AddressComponent
      key={index}
      onPress={setDeliveryAddress(item)}     
      title={["Home", "Οικία"].includes(item.title) ? account.home : ["Work", "Έργασία"].includes(item.title) ? account.work : account.otherLocation}
      street={item.street}
      city={findCity(item)}
      apartment={item.apartment}
      apartmentCaption={common.apartment}
      postcode={item.postcode}
      streetNumber={item.streetNumber}
      active={item.active} 
      item={item}   
      itemIndex={index} 
    />
    );
    
  return (
    <div className="history-container">
    <div className="history-object-container"> 
      <FormControl component="fieldset" style={{width: '100%'}}>
        <FormLabel component="legend"/>
        <RadioGroup aria-label="addresses" name="addresses"  onChange={setDeliveryAddress}>
          {addresses.length !=0 ? addresses.map(renderAddressItem) 
          : <div className="empty-component-text">{account.addAddress}</div> }
        </RadioGroup>
      </FormControl>
      </div>
      <div className="addressAddContainer">
        {showButtons &&
        <div className="addAddressOptions">
          <div className="addAddressOptionsButton">
            <div className="addAddressOptionsButton-label">{account.addNewAddress}</div>
            <RoundedButton
              aria-label='Open new address'
              color={'#00b970'}
              icon={<HomeIcon style={{color: '#fff', fontSize: 30, marginTop: 2}}/>}
              onClick={openNewAddress}
            />
          </div>
          {/* Hidden entirely when the tenant has no maps key — never show an
              affordance that cannot work (same choice as the RN app). */}
          {feathersStore.canGeocode() &&
          <div className="addAddressOptionsButton">
            <div className="addAddressOptionsButton-label">{account.myLocation}</div>
            <RoundedButton
            aria-label='new location'
              color={'#ffa400'}
              icon={<LocationOnIcon style={{color: '#fff', fontSize: 30, marginTop: 2}}/>}
              onClick={onClickLocation || handleMyLocation}
            />
          </div>}
        </div>}
        <RoundedButton
        aria-labelledby='Close Add'
          color={'#0069b9'}
          icon={showButtons?
            <CloseIcon style={{color: '#fff', fontSize: 30, marginTop: 2}}/>
            :
            <AddIcon style={{color: '#fff', fontSize: 30, marginTop: 2}}/>}
          onClick={ClickAddAddress}
        />
      </div> 
      <AddOrEditaddressModal visible={visibleAddNewAddress} close={closeNewAddress} itemToEdit={{}} prefill={prefill}/>
    </div>

  
  );
}

export default inject('feathersStore')(observer(Address));
